import { z } from 'zod';
import { LayoutPartSchema, PinComponentSchema } from '../pin-component';
import { TipTapContentSchema } from '../tiptap-content.schema';
import { BaseInteractiveQuestionPinSettingsSchema, BasePinSchema, BaseTemplateLayoutSchema } from './base-pin';

/**
 * Defines the poll types
 */
export const PollPinTypeValues = ['EMOJI', 'TEXT'] as const;
export const PollPinSettingTypeSchema = z.enum(PollPinTypeValues);
export type PollPinSettingType = z.infer<typeof PollPinSettingTypeSchema>;

/**
 * Defines the available layout options for poll pins
 */
export const PollPinLayoutKeySettingsSchema = z.enum(['TITLE_ONLY']);
export type PollPinLayoutKey = z.infer<typeof PollPinLayoutKeySettingsSchema>;

/**
 * Defines the layout parts available for poll pins
 */
export const PollPinLayoutPartKeySchema = LayoutPartSchema.extract(['TITLE', 'IMAGE1', 'ANSWER_AREA']);

const IdentifiableAnswerSchema = z.object({
  id: z.string().min(1),
});

/**
 * Schema for the emoji poll answer
 */
export const PollPinEmojiAnswerSchema = IdentifiableAnswerSchema.extend({
  text: z.string().min(1),
});

export type PollPinEmojiAnswer = z.infer<typeof PollPinEmojiAnswerSchema>;

/**
 * Schema for the text poll answer
 */
export const PollPinTextAnswerSchema = IdentifiableAnswerSchema.extend({
  text: TipTapContentSchema,
});

export type PollPinTextAnswer = z.infer<typeof PollPinTextAnswerSchema>;

/**
 * Schema for poll pin layout template
 */
export const PollPinLayoutTemplateSchema = BaseTemplateLayoutSchema.extend(
  z.object({
    key: PollPinLayoutKeySettingsSchema,
    layoutParts: z.partialRecord(PollPinLayoutPartKeySchema, PinComponentSchema.optional()),
  }).shape
);
export type PollPinLayoutTemplate = z.infer<typeof PollPinLayoutTemplateSchema>;

/**
 * Base settings schema for all poll types
 * Contains common properties shared across different poll types
 */
const PollPinBaseSettingsSchema = BaseInteractiveQuestionPinSettingsSchema.extend({
  layout: PollPinLayoutKeySettingsSchema,
  layoutParts: z.partialRecord(PollPinLayoutPartKeySchema, z.string().optional()),
});

/**
 * Settings schema for emoji-based polls
 */
export const PollPinSettingsEmojiSchema = PollPinBaseSettingsSchema.extend({
  type: z.literal(PollPinSettingTypeSchema.enum.EMOJI),
  answers: z.array(PollPinEmojiAnswerSchema),
});
export type PollPinSettingsEmoji = z.infer<typeof PollPinSettingsEmojiSchema>;

/**
 * Settings schema for text-based polls
 */
export const PollPinSettingsTextSchema = PollPinBaseSettingsSchema.extend({
  type: z.literal(PollPinSettingTypeSchema.enum.TEXT),
  answers: z.array(PollPinTextAnswerSchema),
});
export type PollPinSettingsText = z.infer<typeof PollPinSettingsTextSchema>;

/**
 * Union type of all possible poll settings
 */
const PollPinSettingsSchema = z.discriminatedUnion('type', [PollPinSettingsEmojiSchema, PollPinSettingsTextSchema]);

export type PollPinSettings = z.infer<typeof PollPinSettingsSchema>;

export const PollPinTypeSchema = z.literal('POLL');

/**
 * Main schema for poll pins
 * Represents a complete poll pin with its type and settings
 */
export const PollPinSchema = BasePinSchema.extend(
  z.object({
    type: PollPinTypeSchema,
    settings: PollPinSettingsSchema,
  }).shape
);
export type PollPin = z.infer<typeof PollPinSchema>;
